import React from "react";
import "../../styles/home.css";
import { withProtectedRoute } from "../../components/ProtectedRoute";
import CustomSnackbar from "../../components/SnackbarDialog";
import { Container, Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { JobFilterSelectors } from "../home/components/JobFilterSelectors";
import { OnSiteTable } from "./components/OnSiteTable";
import { DateTime } from "luxon";
import axiosInstance from "../../utils/api";
import ErrorPage from "../../components/ErrorPage";
import { fetchSystemMetadata } from "../../utils/apiHelpers";
import { JobDetailViewSidePanel } from "../home/components/JobDetailViewSidePanel";
import { RejectionAssignmentModal } from "./components/RejectionAssignmentModal";
import { jobRequestAssignmentService } from "../../services/JobAssignmentRequestService";
import { jobService } from "../../services/JobService";
import { JobEvent, JobListeners } from "../../utils/listeners/JobListeners";
import { withJobFilter } from "../../hoc/filters";
import { isEqual } from "lodash";

// TODO: Type definitions for local component state
/*
interface IOwnState {
    showAlert: boolean;
    alertContent: {
        title: string;
        message: string;
        severity: "success" | "info" | "warning" | "error";
    };
    jobs: unknown[]
    isLoadingJobs: boolean;
    selectedJob?: unknown;
    defaultDate: string | undefined;
    workers: unknown[];
    fullPageError?: unknown;
}
    */

export class OnSitePageComponent extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            showAlert: false,
            alertContent: {
                title: "",
                message: "",
                severity: "success",
            },
            jobs: [],
            isLoadingJobs: false,
            selectedJob: undefined,
            workers: [],
            fullPageError: undefined,
            systemLastUpdated: null,
            rejectionAssignmentModal: null,
            categorizedJobs: {
                Draft: [],
                Accepted: [],
                Rejected: [],
            },
        };
    }

    componentDidMount() {
        JobListeners.setListener(JobEvent.UpdateDateRequested, () => {
            const date = DateTime.fromISO(this.props.isoDate ?? "").toFormat("yyyy-MM-dd");
            this.fetchJobsAndWorkers({
                city: this.props.selectedCity,
                date,
            });
        });
        const date = DateTime.fromISO(this.props.isoDate ?? "").toFormat("yyyy-MM-dd");
        this._fetchJobs({
            city: this.props.selectedCity,
            date,
        });

        this._fetchSystemMetadata();
    }

    componentDidUpdate(prevProps, prevState) {
        if (!isEqual(this.state.jobs, prevState.jobs)) {
            this._categorizeJobs(this.state.jobs);
        }
    }

    componentWillUnmount() {
        JobListeners.removeListener(JobEvent.UpdateDateRequested);
    }

    render() {
        return this.state.fullPageError ? (
            <ErrorPage
                mainHeading="Oops.."
                subHeading={`There was an error fetching the data: ${this.state.fullPageError}`}
            />
        ) : (
            <>
                <CustomSnackbar
                    title={this.state.alertContent.title}
                    message={this.state.alertContent.message}
                    severity={this.state.alertContent.severity}
                    isOpen={this.state.showAlert}
                    onClose={this._hideAlert}
                />
                <Container sx={{ pl: 0 }} maxWidth={false} disableGutters>
                    <JobFilterSelectors onFilterChange={this._fetchJobs} lastUpdated={this.state.systemLastUpdated} />
                    <Container
                        sx={{ mb: 5, background: "white", py: 4, pl: { xs: "16px", md: "100px" } }}
                        maxWidth={false}
                        disableGutters>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={this._handleExportJobs}
                            sx={{ mb: 2 }}>
                            Export to CSV
                        </Button>
                        {this.state.categorizedJobs.Draft.length > 0 ? (
                            <OnSiteTable
                                onJobClick={this._setSelectedJob}
                                isLoading={this.state.isLoadingJobs}
                                jobs={this.state.categorizedJobs.Draft}
                                onSaveAssigmentStatus={this._saveAssigmentStatus}
                                onJobsUpdated={(jobs) => this.setState({ jobs })}
                                onRejectionAssignmentRequest={(job) =>
                                    this.setState({ rejectionAssignmentModal: { job } })
                                }
                                shouldShowFilter={false}
                                shouldShowTableHeader
                                tableLabel={this._getLabel("Draft")}
                            />
                        ) : null}
                        {this.state.categorizedJobs.Rejected.length > 0 ? (
                            <OnSiteTable
                                onJobClick={this._setSelectedJob}
                                isLoading={this.state.isLoadingJobs}
                                jobs={this.state.categorizedJobs.Rejected}
                                onSaveAssigmentStatus={this._saveAssigmentStatus}
                                onJobsUpdated={(jobs) => this.setState({ jobs })}
                                onRejectionAssignmentRequest={(job) =>
                                    this.setState({ rejectionAssignmentModal: { job } })
                                }
                                shouldShowFilter={false}
                                shouldShowTableHeader={this.state.categorizedJobs.Draft.length === 0}
                                tableLabel={this._getLabel("Rejected")}
                            />
                        ) : null}
                        {this.state.categorizedJobs.Accepted.length > 0 ? (
                            <OnSiteTable
                                onJobClick={this._setSelectedJob}
                                isLoading={this.state.isLoadingJobs}
                                jobs={this.state.categorizedJobs.Accepted}
                                onSaveAssigmentStatus={this._saveAssigmentStatus}
                                onJobsUpdated={(jobs) => this.setState({ jobs })}
                                onRejectionAssignmentRequest={(job) =>
                                    this.setState({ rejectionAssignmentModal: { job } })
                                }
                                shouldShowFilter={false}
                                shouldShowTableHeader={
                                    this.state.categorizedJobs.Draft.length === 0 &&
                                    this.state.categorizedJobs.Rejected.length === 0
                                }
                                tableLabel={this._getLabel("Accepted")}
                                onResendSMS={(row) => {
                                    jobRequestAssignmentService.resendJobAssignmentSMSAsync(
                                        row.assignment.assignment_id,
                                    );
                                }}
                            />
                        ) : null}

                        {/* Job Detail View Side Panel */}
                        {this.state.selectedJob ? (
                            <JobDetailViewSidePanel
                                job={this.state.selectedJob}
                                jobs={this.state.jobs}
                                workers={[]}
                                onJobsUpdated={(jobs) => this.setState({ jobs })}
                                closeDetailView={() => this.setState({ selectedJob: undefined })}
                                onSaveAssigmentStatus={this._saveAssigmentStatus}
                                onRejectionAssignmentRequest={(job) =>
                                    this.setState({ rejectionAssignmentModal: { job } })
                                }
                                isAssignmentPage={true}
                            />
                        ) : null}

                        {/* Rejection Assignment Modal */}
                        {this.state.rejectionAssignmentModal ? (
                            <RejectionAssignmentModal
                                job={this.state.rejectionAssignmentModal.job}
                                onClose={() => this.setState({ rejectionAssignmentModal: null })}
                                onSubmit={this._saveAssigmentStatus}
                            />
                        ) : null}
                    </Container>
                </Container>
            </>
        );
    }

    _getLabel = (labelText) => {
        return (
            <div
                style={{
                    width: "100%",
                    height: 0,
                    border: "1px solid rgba(0, 0, 0, 0.2)",
                    flex: "none",
                    order: 0,
                    flexGrow: 0,
                    zIndex: 0,
                    marginTop: "20px",
                    marginBottom: "20px",
                }}>
                <span className="onsiteTableLabel">{labelText}</span>
            </div>
        );
    };

    _categorizeJobs = (jobs) => {
        const categorizedJobs = {
            Draft: [],
            Accepted: [],
            Rejected: [],
        };

        jobs.forEach((job) => {
            if (job.assignment.status === "Draft" || job.assignment.status === "Pending") {
                categorizedJobs.Draft.push(job);
            } else if (job.assignment.status === "Accepted") {
                categorizedJobs.Accepted.push(job);
            } else if (job.assignment.status === "Rejected") {
                categorizedJobs.Rejected.push(job);
            }
        });
        this.setState({ categorizedJobs });
        return categorizedJobs;
    };

    _fetchSystemMetadata = async () => {
        const systemLastUpdated = await fetchSystemMetadata();
        if (systemLastUpdated !== null) {
            this.setState({ systemLastUpdated });
        }
    };

    _fetchJobs = async ({ city, date }) => {
        this.setState({ isLoadingJobs: true, selectedJob: undefined });

        try {
            // Build query parameters dynamically
            const params = new URLSearchParams();
            if (city) params.append("warehouse", city);
            if (date) params.append("date_requested", date ? DateTime.fromISO(date).toFormat("yyyy-MM-dd") : null);
            params.append("is_assignment_page", "true");

            // Fetch job data
            const jobsResponse = await axiosInstance.get(`/api/jobs?${params.toString()}`);
            const jobs = jobsResponse.data?.jobs;
            let date_requested = jobsResponse.data?.date_requested ?? date;

            if (!date_requested) {
                date_requested = DateTime.now().toFormat("yyyy-MM-dd");
            }

            this.setState({ jobs });
        } catch (error) {
            console.error("Error fetching data:", error);
            if (error && error.message) {
                this.setState({ fullPageError: error.message });
            }
        } finally {
            this.setState({ isLoadingJobs: false });
        }
    };

    _hideAlert = () => {
        this.setState({
            showAlert: false,
            alertContent: {
                title: "",
                message: "",
                severity: "success",
            },
        });
    };

    _setSelectedJob = (jobId) => {
        const selectedJob = this.state.jobs.find((job) => job.job_id === jobId);
        this.setState({ selectedJob });
    };

    _handleExportJobs = async () => {
        try {
            const date = DateTime.fromISO(this.props.isoDate ?? "").toFormat("yyyy-MM-dd");
            await jobService.exportJobsToCSV(this.props.selectedCity, date, true);
            this.setState({
                showAlert: true,
                alertContent: {
                    title: "Export Successful",
                    message: "Jobs data has been exported to CSV",
                    severity: "success",
                },
            });
        } catch (error) {
            this.setState({
                showAlert: true,
                alertContent: {
                    title: "Export Failed",
                    message: "Failed to export jobs data",
                    severity: "error",
                },
            });
        }
    };

    _saveAssigmentStatus = async (data) => {
        try {
            let { jobOrder, status, rejection_reason, notes, jobRequestAssignmentID } = data;
            let action = status === "Accepted" ? "accepted" : status === "Rejected" ? "rejected" : "updated";

            await jobRequestAssignmentService.updateAssignmentStatusAsync(
                jobRequestAssignmentID,
                status,
                rejection_reason,
            );

            // Save notes if provided
            if (notes) {
                await jobService.upsertNoteAsync({ job_id: data.job_id, note_text: notes, note_type: "Assignment" });
            }

            const updatedJobs = this.state.jobs.map((job) => {
                if (job.job_order_number === jobOrder) {
                    return {
                        ...job,
                        assignment: {
                            ...job.assignment,
                            status: status,
                            rejection_reason: rejection_reason,
                        },
                        grouped_notes: {
                            ...job.grouped_notes,
                            Assignment: [
                                ...(job.grouped_notes.Assignment || []),
                                {
                                    job_id: job.job_id,
                                    note_text: notes,
                                    note_type: "Assignment",
                                    note_id: jobRequestAssignmentID,
                                },
                            ],
                        },
                    };
                }
                return job;
            });

            this.setState({
                jobs: updatedJobs,
                showAlert: true,
                alertContent: {
                    title: "Change Job Assignment Request Status",
                    message: `The assignment request for job order ${jobOrder} has been ${action}`,
                    severity: "success",
                },
                rejectionAssignmentModal: null,
            });
        } catch (error) {
            // Output error message
            console.error("Failed to save assigment status:", error);
            this.setState({
                showAlert: true,
                alertContent: {
                    title: "Error",
                    message: "Failed to save assigment status",
                    severity: "error",
                },
            });
        }
    };
}

export const OnSitePage = withProtectedRoute(withJobFilter(OnSitePageComponent));
